'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { AlertCircle, SaveIcon, XIcon, ImageIcon, PlugZap, CheckCircle2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { setValidationErrors, getErrorMessage, getVideoUrl } from '@/lib/utils'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PasswordInput } from '@/components/ui/password-input'
import { NumberInput } from '@/components/ui/number-input'
import { FileSelector } from '@/components/partials/files'
import { UnsavedChangesGuardDialog } from '@/components/partials'
import { Combobox } from '@/components/ui/combobox'
import LocaleCombobox from '@/components/ui/locale-combobox'
import { Separator } from '@/components/ui/separator'
import {
  useGeneralSettings,
  useUpdateGeneralSettings,
  usePages,
  useCheckMailSettings,
  useUnsavedChangesGuard
} from '@/hooks'
import { useGeneralSettingsStore, useMetaStore } from '@/store'
import { GeneralSettingsType, GeneralSettingsParams } from '@/types/setting'
import { MailCheckResponseType } from '@/types/tools'
import { FileType } from '@/types/file'
import { MimeTypes } from '@/enums'
import { EMAIL_REGEX } from '@/constants'

const GeneralSettingsForm = () => {
  const t = useTranslations()
  const [formError, setFormError] = useState<string | null>(null)
  const [formDisabled, setFormDisabled] = useState<boolean>(false)
  const [emailLogo, setEmailLogo] = useState<FileType | null>(null)
  const [pdfLogo, setPdfLogo] = useState<FileType | null>(null)
  const [qrLogo, setQrLogo] = useState<FileType | null>(null)
  const [watermarkPicture, setWatermarkPicture] = useState<FileType | null>(null)
  const [welcomeModalImage, setWelcomeModalImage] = useState<FileType | null>(null)
  const [welcomeModalVideo, setWelcomeModalVideo] = useState<FileType | null>(null)
  const [mailCheckResult, setMailCheckResult] = useState<MailCheckResponseType | null>(null)

  const { isLoading: isLoadingSettings, data: settingsData, error: settingsError } = useGeneralSettings()
  const { mutate: updateMutation, isPending: isUpdating } = useUpdateGeneralSettings()
  const { mutate: checkMailMutation, isPending: isCheckingMail } = useCheckMailSettings()
  const { data: pagesData } = usePages({ page: 1, size: 999999 })
  const { metaState } = useMetaStore()

  const formSchema = z.object({
    address: z.string().min(1, { message: t('min_value', { value: 1 }) }),
    phone: z.string().min(1, { message: t('min_value', { value: 1 }) }),
    fax: z.string().optional(),
    email: z
      .string()
      .trim()
      .transform(val => val.toLowerCase())
      .refine(val => EMAIL_REGEX.test(val), {
        message: t('invalid_email')
      }),
    notificationEmail: z
      .string()
      .trim()
      .transform(val => val.toLowerCase())
      .refine(val => EMAIL_REGEX.test(val), {
        message: t('invalid_email')
      }),
    whatsapp: z.string().optional(),
    facebook: z.string().optional(),
    twitter: z.string().optional(),
    instagram: z.string().optional(),
    linkedin: z.string().optional(),
    youtube: z.string().optional(),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
    adminDefaultLocale: z.string().min(1, { message: t('min_value', { value: 1 }) }),
    appDefaultLocale: z.string().min(1, { message: t('min_value', { value: 1 }) }),
    defaultCurrencyCode: z.string().min(1, { message: t('min_value', { value: 1 }) }),
    presentationPage: z.string().optional(),
    emailLogo: z.string().optional(),
    pdfLogo: z.string().optional(),
    qrLogo: z.string().optional(),
    watermarkPicture: z.string().optional(),
    headTrackingCode: z.string().optional(),
    bodyTrackingCode: z.string().optional(),
    activeFormCaptcha: z.boolean().optional(),
    requireUserApproval: z.boolean().optional(),
    defaultTaxRate: z.number().min(0).max(100).optional(),
    mailHost: z.string().optional(),
    mailPort: z.string().optional(),
    mailUsername: z.string().optional(),
    mailSender: z.string().optional(),
    mailProtocol: z.string().optional(),
    mailSmtpAuth: z.boolean().optional(),
    mailSmtpSsl: z.boolean().optional(),
    mailSmtpTls: z.boolean().optional(),
    mailPassword: z.string().optional(),
    welcomeModal: z
      .object({
        enabled: z.boolean().optional(),
        type: z.enum(['image', 'video', 'youtube']).optional(),
        imageId: z.string().optional(),
        link: z.string().optional(),
        videoId: z.string().optional(),
        videoUrl: z.string().optional()
      })
      .optional()
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: '',
      phone: '',
      fax: '',
      email: '',
      notificationEmail: '',
      whatsapp: '',
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      latitude: '',
      longitude: '',
      adminDefaultLocale: '',
      appDefaultLocale: '',
      defaultCurrencyCode: '',
      presentationPage: '',
      emailLogo: '',
      pdfLogo: '',
      qrLogo: '',
      watermarkPicture: '',
      headTrackingCode: '',
      bodyTrackingCode: '',
      activeFormCaptcha: false,
      requireUserApproval: false,
      defaultTaxRate: 0,
      mailHost: '',
      mailPort: '',
      mailUsername: '',
      mailSender: '',
      mailProtocol: 'smtp',
      mailSmtpAuth: false,
      mailSmtpSsl: false,
      mailSmtpTls: false,
      mailPassword: '',
      welcomeModal: {
        enabled: false,
        type: 'image',
        imageId: '',
        link: '',
        videoId: '',
        videoUrl: ''
      }
    }
  })

  const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

  useEffect(() => {
    if (settingsData) {
      // Backend'den gelen GeneralSettingsType'ı GeneralSettingsParams'a çevir
      const formData: GeneralSettingsParams = {
        ...settingsData,
        presentationPage: settingsData.presentationPage?.id || '',
        emailLogo: settingsData.emailLogo?.id || '',
        pdfLogo: settingsData.pdfLogo?.id || '',
        qrLogo: settingsData.qrLogo?.id || '',
        watermarkPicture: settingsData.watermarkPicture?.id || '',
        mailPassword: settingsData.mailPassword || '',
        welcomeModal: settingsData.welcomeModal
          ? {
              enabled: settingsData.welcomeModal.enabled,
              type:
                ((settingsData.welcomeModal.type as string)?.toLowerCase() as 'image' | 'video' | 'youtube') || 'image',
              imageId: settingsData.welcomeModal.image?.id || '',
              link: settingsData.welcomeModal.link || '',
              videoId: settingsData.welcomeModal.video?.id || '',
              videoUrl: settingsData.welcomeModal.videoUrl || ''
            }
          : { enabled: false, type: 'image', imageId: '', link: '', videoId: '', videoUrl: '' }
      }

      // Set picture states
      setEmailLogo(settingsData.emailLogo || null)
      setPdfLogo(settingsData.pdfLogo || null)
      setQrLogo(settingsData.qrLogo || null)
      setWatermarkPicture(settingsData.watermarkPicture || null)
      setWelcomeModalImage(settingsData.welcomeModal?.image || null)
      setWelcomeModalVideo(settingsData.welcomeModal?.video || null)

      form.reset(formData as z.infer<typeof formSchema>)
    }
  }, [settingsData, form])

  useEffect(() => {
    if (settingsError) {
      const message = getErrorMessage(settingsError, t('something_went_wrong'))
      setFormError(message)
      toast.error(message)
    }
  }, [settingsError, t])

  // Backend check endpoint field names → form field names
  const mailCheckFieldMap: Record<string, string> = {
    host: 'mailHost',
    port: 'mailPort',
    username: 'mailUsername',
    password: 'mailPassword',
    protocol: 'mailProtocol',
    smtpAuth: 'mailSmtpAuth',
    smtpSsl: 'mailSmtpSsl',
    smtpTls: 'mailSmtpTls'
  }

  const handleCheckMail = () => {
    setMailCheckResult(null)
    const values = form.getValues()
    checkMailMutation(
      {
        host: values.mailHost || '',
        port: parseInt(values.mailPort || '0', 10),
        username: values.mailUsername || '',
        password: values.mailPassword || '',
        protocol: values.mailProtocol || 'smtp',
        smtpAuth: values.mailSmtpAuth ?? false,
        smtpSsl: values.mailSmtpSsl ?? false,
        smtpTls: values.mailSmtpTls ?? false
      },
      {
        onSuccess: (result: MailCheckResponseType) => {
          setMailCheckResult(result)
          if (result.success) {
            toast.success(result.message || t('mail_connection_success'))
          } else {
            toast.error(result.message || t('mail_connection_failed'))
          }
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            const items = error.response?.data?.items || {}
            Object.entries(items).forEach(([key, message]) => {
              const fieldName = mailCheckFieldMap[key] || key
              form.setError(fieldName as any, { type: 'manual', message: message as string })
            })
          }
        }
      }
    )
  }

  const handleEmailLogoChange = (files: FileType | FileType[] | null) => {
    if (!files) {
      setEmailLogo(null)
      form.setValue('emailLogo', '', { shouldValidate: true })
    } else if (Array.isArray(files)) {
      setEmailLogo(files[0] || null)
      form.setValue('emailLogo', files[0]?.id || '', { shouldValidate: true })
    } else {
      setEmailLogo(files)
      form.setValue('emailLogo', files?.id || '', { shouldValidate: true })
    }
  }

  const handlePdfLogoChange = (files: FileType | FileType[] | null) => {
    if (!files) {
      setPdfLogo(null)
      form.setValue('pdfLogo', '', { shouldValidate: true })
    } else if (Array.isArray(files)) {
      setPdfLogo(files[0] || null)
      form.setValue('pdfLogo', files[0]?.id || '', { shouldValidate: true })
    } else {
      setPdfLogo(files)
      form.setValue('pdfLogo', files?.id || '', { shouldValidate: true })
    }
  }

  const handleQrLogoChange = (files: FileType | FileType[] | null) => {
    if (!files) {
      setQrLogo(null)
      form.setValue('qrLogo', '', { shouldValidate: true })
    } else if (Array.isArray(files)) {
      setQrLogo(files[0] || null)
      form.setValue('qrLogo', files[0]?.id || '', { shouldValidate: true })
    } else {
      setQrLogo(files)
      form.setValue('qrLogo', files?.id || '', { shouldValidate: true })
    }
  }

  const handleWatermarkPictureChange = (files: FileType | FileType[] | null) => {
    if (!files) {
      setWatermarkPicture(null)
      form.setValue('watermarkPicture', '', { shouldValidate: true })
    } else if (Array.isArray(files)) {
      setWatermarkPicture(files[0] || null)
      form.setValue('watermarkPicture', files[0]?.id || '', { shouldValidate: true })
    } else {
      setWatermarkPicture(files)
      form.setValue('watermarkPicture', files?.id || '', { shouldValidate: true })
    }
  }

  const handleWelcomeModalImageChange = (files: FileType | FileType[] | null) => {
    if (!files) {
      setWelcomeModalImage(null)
      form.setValue('welcomeModal.imageId', '', { shouldValidate: true })
    } else if (Array.isArray(files)) {
      setWelcomeModalImage(files[0] || null)
      form.setValue('welcomeModal.imageId', files[0]?.id || '', { shouldValidate: true })
    } else {
      setWelcomeModalImage(files)
      form.setValue('welcomeModal.imageId', files?.id || '', { shouldValidate: true })
    }
  }

  const handleWelcomeModalVideoChange = (files: FileType | FileType[] | null) => {
    if (!files) {
      setWelcomeModalVideo(null)
      form.setValue('welcomeModal.videoId', '', { shouldValidate: true })
    } else if (Array.isArray(files)) {
      setWelcomeModalVideo(files[0] || null)
      form.setValue('welcomeModal.videoId', files[0]?.id || '', { shouldValidate: true })
    } else {
      setWelcomeModalVideo(files)
      form.setValue('welcomeModal.videoId', files?.id || '', { shouldValidate: true })
    }
  }

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    updateMutation(data as GeneralSettingsParams, {
      onSuccess: async (updatedSettings: GeneralSettingsType) => {
        // Updated settings'I store state'ine set et
        useGeneralSettingsStore.setState({
          generalSettingsState: {
            isLoading: false,
            response: updatedSettings,
            error: null
          }
        })
        // Meta store's refecth
        const { getMeta } = useMetaStore.getState()
        await getMeta()
        setFormError(null)
        toast.success(t('settings_updated_successfully'))
      },
      onError: (error: any) => {
        const message = getErrorMessage(error, t('something_went_wrong'))
        if (error.response?.status === 422) {
          setValidationErrors(error, form.setError)
          setFormError(null)
        } else {
          setFormError(message)
        }
      }
    })
  }

  const handleCancel = () => {
    if (settingsData) {
      form.reset(settingsData as z.infer<typeof formSchema>)
    }
  }

  useEffect(() => {
    if (isLoadingSettings || isUpdating) {
      setFormDisabled(true)
    } else {
      setFormDisabled(false)
    }
  }, [isLoadingSettings, isUpdating])

  if (isLoadingSettings) {
    const fieldRow = (cols: number) => (
      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-${Math.min(cols, 3)} xl:grid-cols-${cols}`}>
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    )
    const sectionTitle = (w: string) => <Skeleton className={`h-6 ${w}`} />
    return (
      <div className="flex flex-col gap-6">
        <div className="grid gap-5">
          {sectionTitle('w-48')}
          {fieldRow(4)}
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-36')}
          {fieldRow(4)}
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-40')}
          {fieldRow(2)}
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-40')}
          {fieldRow(2)}
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-32')}
          {fieldRow(1)}
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-24')}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-32 w-full rounded-md" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-32 w-full rounded-md" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-32 w-full rounded-md" />
            </div>
          </div>
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-40')}
          {fieldRow(4)}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-6 w-10 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-36')}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
            ))}
          </div>
          <Skeleton className="h-px w-full" />
          {sectionTitle('w-36')}
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-10 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-px w-full" />
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Form {...form}>
        {formError && (
          <Alert variant="destructive" className="mb-4" onClose={() => setFormError(null)}>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('error')}</AlertTitle>
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-6">
          <div className="grid gap-5">
            <h3 className="text-lg font-medium">{t('contact_information')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('address')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('address')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('phone')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('phone')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fax"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fax')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('fax')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('email')}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={t('email')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="notificationEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('notification_email')}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={t('notification_email')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('social_media')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="whatsapp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WhatsApp</FormLabel>
                    <FormControl>
                      <Input placeholder="WhatsApp" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="facebook"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Facebook</FormLabel>
                    <FormControl>
                      <Input placeholder="Facebook" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="twitter"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Twitter</FormLabel>
                    <FormControl>
                      <Input placeholder="Twitter" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="instagram"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instagram</FormLabel>
                    <FormControl>
                      <Input placeholder="Instagram" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn</FormLabel>
                    <FormControl>
                      <Input placeholder="LinkedIn" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="youtube"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>YouTube</FormLabel>
                    <FormControl>
                      <Input placeholder="YouTube" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('map_settings')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('latitude')}</FormLabel>
                    <FormControl>
                      <NumberInput
                        placeholder={t('latitude')}
                        min={-90}
                        max={90}
                        decimalScale={6}
                        stepper={0.000001}
                        disabled={formDisabled}
                        value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                        onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('longitude')}</FormLabel>
                    <FormControl>
                      <NumberInput
                        placeholder={t('longitude')}
                        min={-180}
                        max={180}
                        decimalScale={6}
                        stepper={0.000001}
                        disabled={formDisabled}
                        value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                        onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('locale_settings')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="adminDefaultLocale"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>{t('admin_default_locale')}</FormLabel>
                    <FormControl>
                      <LocaleCombobox
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                        clearable={false}
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="appDefaultLocale"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>{t('app_default_locale')}</FormLabel>
                    <FormControl>
                      <LocaleCombobox
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                        clearable={false}
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultCurrencyCode"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>{t('default_currency')}</FormLabel>
                    <FormControl>
                      <Combobox
                        options={(metaState.response?.currencies || []).map(currency => ({
                          label: `${currency.name} (${currency.code})`,
                          value: currency.code,
                          description: currency.symbol,
                          searchValue: `${currency.name} ${currency.code} ${currency.symbol}`
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                        clearable={false}
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultTaxRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('default_tax_rate')}</FormLabel>
                    <FormControl>
                      <NumberInput
                        min={0}
                        max={100}
                        decimalScale={2}
                        stepper={0.01}
                        suffix="%"
                        placeholder="0.00"
                        disabled={formDisabled}
                        value={field.value ?? 0}
                        onValueChange={v => field.onChange(v ?? 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('pages')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="presentationPage"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('presentation_page')}</FormLabel>
                    <FormControl>
                      <Combobox
                        options={(pagesData?.items || []).map(page => ({
                          label: Object.values(page.translations)[0]?.title || `Page ${page.id}`,
                          value: page.id.toString(),
                          searchValue: Object.values(page.translations)
                            .map((translation: any) => translation.title)
                            .join(' ')
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                        clearable={true}
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('logos')}</h3>
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="emailLogo"
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h4 className="font-semibold">{t('email_logo')}</h4>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={emailLogo}
                    onChange={handleEmailLogoChange}
                    buttonLabel={t('select_logo')}
                    buttonLabelChange={t('change_logo')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={settingsData?.emailLogo?.id}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="pdfLogo"
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h4 className="font-semibold">{t('pdf_logo')}</h4>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={pdfLogo}
                    onChange={handlePdfLogoChange}
                    buttonLabel={t('select_logo')}
                    buttonLabelChange={t('change_logo')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={settingsData?.pdfLogo?.id}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="qrLogo"
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h4 className="font-semibold">{t('qr_logo')}</h4>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={qrLogo}
                    onChange={handleQrLogoChange}
                    buttonLabel={t('select_logo')}
                    buttonLabelChange={t('change_logo')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={settingsData?.qrLogo?.id}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="watermarkPicture"
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h4 className="font-semibold">{t('watermark_picture')}</h4>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={watermarkPicture}
                    onChange={handleWatermarkPictureChange}
                    buttonLabel={t('select_file')}
                    buttonLabelChange={t('change_file')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={settingsData?.watermarkPicture?.id}
                  />
                </div>
              </div>
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('mail_settings')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="mailHost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('mail_host')}</FormLabel>
                    <FormControl>
                      <Input placeholder="smtp.gmail.com" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailPort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('mail_port')}</FormLabel>
                    <FormControl>
                      <NumberInput
                        placeholder="587"
                        min={1}
                        max={65535}
                        decimalScale={0}
                        stepper={1}
                        disabled={formDisabled}
                        value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                        onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('mail_username')}</FormLabel>
                    <FormControl>
                      <Input placeholder="no-reply@example.com" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('mail_password')}</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="••••••••" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailSender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('mail_sender')}</FormLabel>
                    <FormControl>
                      <Input placeholder="My App" disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailProtocol"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('mail_protocol')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={formDisabled}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('mail_protocol')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="smtp">SMTP</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="mailSmtpAuth"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center space-x-2">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} disabled={formDisabled} />
                      </FormControl>
                      <FormLabel>{t('mail_smtp_auth')}</FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailSmtpSsl"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center space-x-2">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} disabled={formDisabled} />
                      </FormControl>
                      <FormLabel>{t('mail_smtp_ssl')}</FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mailSmtpTls"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center space-x-2">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} disabled={formDisabled} />
                      </FormControl>
                      <FormLabel>{t('mail_smtp_tls')}</FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckMail}
                  disabled={formDisabled || isCheckingMail}
                  loading={isCheckingMail}>
                  <PlugZap className="h-4 w-4" />
                  {t('check_mail_connection')}
                </Button>
              </div>
              {mailCheckResult && (
                <Alert variant={mailCheckResult.success ? 'default' : 'destructive'} className="max-w-lg">
                  {mailCheckResult.success ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  <AlertTitle>
                    {mailCheckResult.success ? t('mail_connection_success') : t('mail_connection_failed')}
                  </AlertTitle>
                  <AlertDescription>
                    {mailCheckResult.message} ({mailCheckResult.host}:{mailCheckResult.port})
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('welcome_modal')}</h3>
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="welcomeModal.enabled"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-start space-x-2">
                      <FormControl>
                        <Switch checked={!!field.value} onCheckedChange={field.onChange} disabled={formDisabled} />
                      </FormControl>
                      <FormLabel className="mt-0.5">{t('welcome_modal_enabled')}</FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch('welcomeModal.enabled') && (
                <>
                  <FormField
                    control={form.control}
                    name="welcomeModal.type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('welcome_modal_type')}</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          defaultValue="image"
                          disabled={formDisabled}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('welcome_modal_type')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="image">{t('welcome_modal_type_image')}</SelectItem>
                            <SelectItem value="video">{t('welcome_modal_type_video')}</SelectItem>
                            <SelectItem value="youtube">{t('welcome_modal_type_youtube')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.watch('welcomeModal.type') === 'image' && (
                    <>
                      <div className="space-y-2">
                        <FormField
                          control={form.control}
                          name="welcomeModal.imageId"
                          render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                        />
                        <FormLabel>{t('welcome_modal_image')}</FormLabel>
                        <FileSelector
                          multiple={false}
                          onlyImages={true}
                          optimizeImageSelection={true}
                          value={welcomeModalImage}
                          onChange={handleWelcomeModalImageChange}
                          buttonLabel={t('select_file')}
                          buttonLabelChange={t('change_file')}
                          noSelectionText={t('no_file_selected')}
                          allowMainSelection={true}
                          initialMainId={settingsData?.welcomeModal?.image?.id}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="welcomeModal.link"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('welcome_modal_link')}</FormLabel>
                            <FormControl>
                              <Input placeholder="https://example.com" disabled={formDisabled} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                  {form.watch('welcomeModal.type') === 'video' && (
                    <div className="space-y-2">
                      <FormField
                        control={form.control}
                        name="welcomeModal.videoId"
                        render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                      />
                      <FormLabel>{t('welcome_modal_video_file')}</FormLabel>
                      <FileSelector
                        multiple={false}
                        onlyImages={false}
                        optimizeImageSelection={false}
                        types={[MimeTypes.MP4]}
                        value={welcomeModalVideo}
                        onChange={handleWelcomeModalVideoChange}
                        buttonLabel={t('select_file')}
                        buttonLabelChange={t('change_file')}
                        noSelectionText={t('no_file_selected')}
                      />
                      {welcomeModalVideo?.path && (
                        <video
                          controls
                          className="max-h-[320px] w-full rounded-lg border"
                          src={getVideoUrl(welcomeModalVideo.path)}
                        />
                      )}
                    </div>
                  )}
                  {form.watch('welcomeModal.type') === 'youtube' && (
                    <FormField
                      control={form.control}
                      name="welcomeModal.videoUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('welcome_modal_video_url')}</FormLabel>
                          <FormControl>
                            <Input placeholder="https://www.youtube.com/embed/..." disabled={formDisabled} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('tracking_code')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="headTrackingCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('head_tracking_code')}</FormLabel>
                    <FormControl>
                      <Textarea placeholder={t('head_tracking_code')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bodyTrackingCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('body_tracking_code')}</FormLabel>
                    <FormControl>
                      <Textarea placeholder={t('body_tracking_code')} disabled={formDisabled} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <h3 className="mt-4 text-lg font-medium">{t('other_settings')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <FormField
                control={form.control}
                name="activeFormCaptcha"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-start space-x-2">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} disabled={formDisabled} />
                      </FormControl>
                      <FormLabel className="mt-0.5">{t('active_form_captcha')}</FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="requireUserApproval"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-start space-x-2">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} disabled={formDisabled} />
                      </FormControl>
                      <div className="mt-0.5 flex flex-col gap-2">
                        <FormLabel>{t('require_user_approval')}</FormLabel>
                        <p className="text-muted-foreground text-xs">{t('require_user_approval_description')}</p>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          <div className="flex flex-row items-center justify-between space-x-2">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isUpdating || formDisabled}>
              <XIcon className="h-4 w-4" />
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={isUpdating || formDisabled} loading={isUpdating || isLoadingSettings}>
              <SaveIcon className="h-4 w-4" />
              {t('save')}
            </Button>
          </div>
        </div>
      </Form>
      <UnsavedChangesGuardDialog open={isNavigationBlocked} onConfirm={confirmNavigation} onCancel={cancelNavigation} />
    </form>
  )
}

export default GeneralSettingsForm
